import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronDown, Loader2, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EDUCATION_OPTIONS = ['8th–10th Grade', '11th–12th Grade', 'UG Student', 'Graduate', 'Working Professional', 'Other'];

const inputCls =
  'w-full rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-sm text-ink placeholder:text-slate-400 transition-colors focus:border-coral focus:outline-none focus:ring-2 focus:ring-coral/30';

const empty = { name: '', email: '', phone: '', education: '', interest: '', message: '' };

const LeadForm = ({ source, showMessage = false, buttonLabel = 'Submit', interestLabel = 'Career Interest', dark = false }) => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, source, message: showMessage ? form.message : null });
      toast.success("Thanks! We've received your details. Our team will reach out soon.");
      setForm(empty);
    } catch {
      toast.error('Something went wrong. Please try again in a moment.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid={`lead-form-${source}`}>
      <div className="grid gap-4 sm:grid-cols-2">
        <input required className={inputCls} placeholder="Your Name" value={form.name} onChange={update('name')} data-testid="lead-form-name-input" aria-label="Name" />
        <input required type="email" className={inputCls} placeholder="Email Address" value={form.email} onChange={update('email')} data-testid="lead-form-email-input" aria-label="Email" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <input required type="tel" className={inputCls} placeholder="Phone Number" value={form.phone} onChange={update('phone')} data-testid="lead-form-phone-input" aria-label="Phone" />
        <div className="relative">
          <select required className={`${inputCls} appearance-none pr-10 ${form.education ? '' : 'text-slate-400'}`} value={form.education} onChange={update('education')} data-testid="lead-form-education-select" aria-label="Current education">
            <option value="" disabled>Current Education</option>
            {EDUCATION_OPTIONS.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
          <ChevronDown className="pointer-events-none absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        </div>
      </div>
      <input required className={inputCls} placeholder={`${interestLabel} (e.g. Data Science, MBBS, Design)`} value={form.interest} onChange={update('interest')} data-testid="lead-form-interest-input" aria-label={interestLabel} />
      {showMessage && (
        <textarea
          className={`${inputCls} min-h-[110px] resize-y`}
          placeholder="Tell us a little about your situation or question…"
          value={form.message}
          onChange={update('message')}
          data-testid="lead-form-message-input"
          aria-label="Message"
        />
      )}
      <button
        type="submit"
        disabled={loading}
        data-testid="lead-form-submit-button"
        className="flex w-full items-center justify-center gap-2 rounded-full bg-coral py-4 text-base font-semibold text-white transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-coral-deep disabled:cursor-not-allowed disabled:opacity-60"
      >
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-4 w-4" />}
        {loading ? 'Submitting…' : buttonLabel}
      </button>
      <p className={`text-center text-xs ${dark ? 'text-white/50' : 'text-slate-400'}`}>
        Your details are safe with us. We only use them to help with your career journey.
      </p>
    </form>
  );
};

export default LeadForm;
